import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardList, Loader2, RefreshCw } from "lucide-react";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import HrmDashboardBranchPicker, {
  buildHrmDashboardBranchSelectOptions,
} from "../shared/hrmDashboardBranchSelect";
import { getAttendanceConfirmationActivityLog } from "../../../api/hrm/employees/attendanceConfirmation";
import { formatDateTime } from "../../../utils/formatUtils";
import { hasPermission } from "../../../utils/permissionUtils";
import { showErrorToast } from "../../../components/ConfirmDialog";

const monthDefault = () => {
  const n = new Date();
  const d = new Date(n.getFullYear(), n.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const KIND_META = {
  request: {
    label: "Yêu cầu kỳ",
    className: "bg-blue-50 text-blue-900 ring-1 ring-blue-100",
  },
  close: {
    label: "Đóng kỳ",
    className: "bg-amber-50 text-amber-900 ring-1 ring-amber-100",
  },
  explanation: {
    label: "Giải trình",
    className: "bg-violet-50 text-violet-900 ring-1 ring-violet-100",
  },
  acknowledge: {
    label: "Đồng ý bảng công",
    className: "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-100",
  },
  revoke: {
    label: "Huỷ xác nhận",
    className: "bg-rose-50 text-rose-900 ring-1 ring-rose-100",
  },
};

function relatedPeopleBlock(ev, isHrView) {
  const lines = [];
  if (ev.actor_name) {
    lines.push(
      <span key="actor">
        <span className="font-semibold text-gray-600">Thực hiện: </span>
        {ev.actor_name}
      </span>
    );
  }
  if (ev.employee_name) {
    lines.push(
      <span key="emp">
        <span className="font-semibold text-gray-600">Nhân viên: </span>
        {ev.employee_name}
      </span>
    );
  }
  if (isHrView && ev.branch_name) {
    lines.push(
      <span key="br">
        <span className="font-semibold text-gray-600">Chi nhánh: </span>
        {ev.branch_name}
      </span>
    );
  }
  if (!lines.length) {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <div className="flex min-w-0 flex-col gap-1 break-words text-[13px] leading-snug text-gray-700">
      {lines.map((node, i) => (
        <span key={i} className="min-w-0 break-words">
          {node}
        </span>
      ))}
    </div>
  );
}

const AttendanceConfirmationActivityTab = () => {
  const [monthValue, setMonthValue] = useState(monthDefault());
  const [branchRecords, setBranchRecords] = useState([]);
  const [listBranchId, setListBranchId] = useState("all");
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  const canHrBranchFilter = hasPermission("hrm.attendance.confirm", "C");

  const branchSelectOptions = useMemo(
    () => buildHrmDashboardBranchSelectOptions(branchRecords),
    [branchRecords]
  );

  useEffect(() => {
    getAllBranches_Array()
      .then((list) => setBranchRecords(Array.isArray(list) ? list : []))
      .catch(console.error);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const branch_id =
        canHrBranchFilter && listBranchId && listBranchId !== "all" ? listBranchId : undefined;
      const data = await getAttendanceConfirmationActivityLog({
        year_month: monthValue,
        ...(branch_id ? { branch_id } : {}),
      });
      setPayload(data);
    } catch (e) {
      console.error(e);
      setPayload(null);
      showErrorToast(e?.response?.data?.message || e?.message || "Không tải được nhật ký.");
    } finally {
      setLoading(false);
    }
  }, [monthValue, listBranchId, canHrBranchFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const events = payload?.events || [];
  const period = payload?.period || null;
  const isHrView = Boolean(payload?.is_hr_view);

  return (
    <div className="mx-auto min-w-0 max-w-full space-y-4" data-tour="hrm-attendance-activity-root">
      <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm sm:p-4 md:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-2 sm:gap-3">
            <div className="shrink-0 rounded-2xl bg-red-50 p-2.5 text-red-700 sm:p-3">
              <ClipboardList className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-black uppercase tracking-tight text-gray-800 sm:text-lg">
                Nhật ký xác nhận / giải trình
              </h2>
              <p className="mt-1 text-xs leading-relaxed break-words text-gray-500 sm:text-sm">
                {isHrView
                  ? "Theo tháng: sự kiện kỳ (gửi yêu cầu, đóng kỳ), giải trình và xác nhận đồng ý bảng công của nhân viên trong phạm vi (có thể lọc chi nhánh)."
                  : "Theo tháng: sự kiện kỳ áp dụng cho mọi người, cùng giải trình và xác nhận đồng ý của bạn."}
              </p>
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-shrink-0 flex-row flex-wrap items-end justify-end gap-2 sm:gap-3 lg:w-auto lg:max-w-none">
            {canHrBranchFilter ? (
              <div className="min-w-0 w-full sm:w-auto sm:min-w-[12rem] sm:max-w-[18rem]">
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                  Lọc chi nhánh (tuỳ chọn)
                </label>
                <HrmDashboardBranchPicker
                  instanceId="hrm-attendance-activity-branch"
                  classNamePrefix="hrm-dash-branch-att-activity"
                  className="w-full"
                  compactControlHeight
                  options={branchSelectOptions}
                  value={listBranchId}
                  onChange={setListBranchId}
                  aria-label="Lọc chi nhánh nhật ký xác nhận giờ công"
                />
              </div>
            ) : null}
            <div className="flex min-w-0 flex-1 items-end gap-2 sm:w-[11.25rem] sm:flex-initial sm:gap-2">
              <div className="min-w-0 flex-1 space-y-1 sm:w-full">
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                  Tháng
                </label>
                <input
                  type="month"
                  value={monthValue}
                  onChange={(e) => setMonthValue(e.target.value)}
                  className="h-11 min-h-[2.75rem] w-full min-w-0 max-w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-700 outline-none focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-100 sm:max-w-[11.25rem]"
                />
              </div>
              <button
                type="button"
                onClick={() => load()}
                disabled={loading}
                title="Làm mới"
                aria-label="Làm mới nhật ký"
                className="inline-flex h-11 min-h-[2.75rem] min-w-[2.75rem] shrink-0 items-center justify-center self-end rounded-xl border border-gray-200 bg-white text-gray-700 hover:border-red-200 hover:bg-red-50 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2.2} />
                ) : (
                  <RefreshCw className="h-5 w-5" strokeWidth={2.2} />
                )}
              </button>
            </div>
          </div>
        </div>

        {period?.employee_response_deadline_at ? (
          <p className="mt-3 text-[11px] font-semibold text-gray-600">
            Hạn NV đã gắn kỳ: {formatDateTime(period.employee_response_deadline_at)}
          </p>
        ) : null}

        <div className="mt-4 min-w-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50/80 p-3 sm:p-4">
          {loading && !payload ? (
            <div className="flex justify-center py-12 text-gray-400">
              <Loader2 className="h-8 w-8 animate-spin" strokeWidth={2.2} />
            </div>
          ) : events.length === 0 ? (
            <p className="text-center text-sm font-semibold text-gray-500">
              {period
                ? "Chưa có dòng nhật ký cho tháng này (chưa gửi yêu cầu, chưa giải trình hoặc chưa xác nhận đồng ý)."
                : "Chưa có kỳ bảng công cho tháng đã chọn."}
            </p>
          ) : (
            <>
              <div className="touch-pan-y space-y-3 md:hidden">
                {events.map((ev, idx) => {
                  const meta = KIND_META[ev.kind] || {
                    label: ev.kind || "—",
                    className: "bg-gray-50 text-gray-800 ring-1 ring-gray-100",
                  };
                  return (
                    <article
                      key={ev.id}
                      className={`rounded-xl border border-gray-100 p-3 shadow-sm ${
                        idx % 2 === 1 ? "bg-white" : "bg-gray-50/90"
                      }`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-200/80 pb-2">
                        <time className="text-xs font-semibold text-gray-700">
                          {formatDateTime(ev.at)}
                        </time>
                        <span
                          className={`inline-flex max-w-full shrink-0 rounded-lg px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${meta.className}`}
                        >
                          {meta.label}
                        </span>
                      </div>
                      <div className="mt-2 min-w-0">
                        <p className="break-words font-bold text-gray-900">{ev.title}</p>
                        {ev.detail ? (
                          <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-600">
                            {ev.detail}
                          </p>
                        ) : null}
                      </div>
                      <div className="mt-3 border-t border-gray-100 pt-2">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
                          {isHrView ? "Người / chi nhánh" : "Người liên quan"}
                        </p>
                        <div className="mt-1 min-w-0">{relatedPeopleBlock(ev, isHrView)}</div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="hidden min-w-0 md:block md:overflow-x-auto md:rounded-lg">
                <table className="w-full min-w-[720px] table-fixed text-left text-sm">
                  <colgroup>
                    <col className="w-[9.5rem]" />
                    <col className="w-[8.5rem]" />
                    <col />
                    <col className="w-[14rem]" />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-gray-200 text-[11px] font-black uppercase tracking-wide text-gray-400">
                      <th className="py-2.5 pr-3">Thời gian</th>
                      <th className="py-2.5 pr-3">Loại</th>
                      <th className="py-2.5 pr-3">Nội dung</th>
                      <th className="py-2.5 pr-0">
                        {isHrView ? "Người / chi nhánh" : "Người liên quan"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev, idx) => {
                      const meta = KIND_META[ev.kind] || {
                        label: ev.kind || "—",
                        className: "bg-gray-50 text-gray-800 ring-1 ring-gray-100",
                      };
                      return (
                        <tr
                          key={ev.id}
                          className={`border-b border-gray-100 align-top ${
                            idx % 2 === 1 ? "bg-white" : "bg-gray-50/60"
                          }`}
                        >
                          <td className="overflow-hidden py-3 pr-3 text-xs font-semibold text-gray-700">
                            <span className="block break-words">{formatDateTime(ev.at)}</span>
                          </td>
                          <td className="overflow-hidden py-3 pr-3">
                            <span
                              className={`inline-flex max-w-full rounded-lg px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${meta.className}`}
                            >
                              {meta.label}
                            </span>
                          </td>
                          <td className="min-w-0 overflow-hidden py-3 pr-3">
                            <p className="break-words font-bold text-gray-900">{ev.title}</p>
                            {ev.detail ? (
                              <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-600">
                                {ev.detail}
                              </p>
                            ) : null}
                          </td>
                          <td className="min-w-0 overflow-hidden py-3 text-[13px] text-gray-700">
                            {relatedPeopleBlock(ev, isHrView)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {events.length >= 500 ? (
            <p className="mt-3 text-center text-[11px] font-semibold text-amber-800">
              Chỉ hiển thị tối đa 500 dòng gần nhất — thu hẹp tháng hoặc lọc chi nhánh nếu cần.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AttendanceConfirmationActivityTab;
